import React, { Suspense, lazy } from 'react';
import { AnimatePresence } from 'framer-motion';
import Celebration from './components/Celebration.jsx';
import { IconConfigProvider } from './contexts/IconConfigContext.jsx';
import { GlobalControls } from './components/GlobalControls.jsx';
import { WelcomeScreen } from './components/screens/WelcomeScreen.jsx';
// The puzzle-modal layer pulls in JourneyTab + framer-motion; defer it so that
// game code stays out of the initial bundle. (It's always mounted, not inside
// an AnimatePresence, so a Suspense wrapper is safe here.)
const GameModals = lazy(() =>
  import('./components/GameModals.jsx').then((m) => ({ default: m.GameModals })),
);
// Game bundle (framer-motion-heavy + all game subcomponents) is lazy so the
// Welcome screen renders fast on first load.
const GameScreen = lazy(() =>
  import('./components/screens/GameScreen.jsx').then((m) => ({ default: m.GameScreen })),
);
import { RSVPScreen } from './components/screens/RSVPScreen.jsx';
import { GuestbookScreen } from './components/screens/GuestbookScreen.jsx';
// Edit-RSVP is reached from a token link in the confirmation email; lazy so it
// (and its Firebase-free fetch flow) stays off the initial bundle.
const EditRsvpScreen = lazy(() =>
  import('./components/screens/EditRsvpScreen.jsx').then((m) => ({ default: m.EditRsvpScreen })),
);
// Admin is a rarely-visited route and the only eager screen that pulls in
// Firebase — lazy-load it so Firebase stays out of the initial bundle.
const AdminScreen = lazy(() =>
  import('./components/screens/AdminScreen.jsx').then((m) => ({ default: m.AdminScreen })),
);
// Day-of features (hub + gallery/photo booth/livestream/timeline) are only
// relevant on the wedding day itself — lazy so RSVP-ing guests never
// download this code before Sept 19.
const GalleryScreen = lazy(() =>
  import('./components/screens/GalleryScreen.jsx').then((m) => ({ default: m.GalleryScreen })),
);
const PhotoBoothScreen = lazy(() =>
  import('./components/screens/PhotoBoothScreen.jsx').then((m) => ({ default: m.PhotoBoothScreen })),
);
const LivestreamScreen = lazy(() =>
  import('./components/screens/LivestreamScreen.jsx').then((m) => ({ default: m.LivestreamScreen })),
);
const TimelineScreen = lazy(() =>
  import('./components/screens/TimelineScreen.jsx').then((m) => ({ default: m.TimelineScreen })),
);
import { useDarkMode } from './hooks/useDarkMode.jsx';
import { useRoute } from './hooks/useRoute.js';
import { useWeddingPhase } from './hooks/useWeddingPhase.js';
import { useDragScroll } from './hooks/useDragScroll.jsx';
import { useGameState } from './hooks/useGameState.jsx';
import { focusFor } from './components/landing/phase.js';

/**
 * Top-level shell. Composes the four screens, the floating global controls,
 * and the game-driven modal layer. Each concern lives in its own hook or file:
 *
 * - useRoute → flowStep + clean-path URL sync
 * - useDarkMode  → theme toggle + persistence + <html class="dark">
 * - useDragScroll → click/touch drag on the game board + parallax motion values
 * - useGameState → the game state machine, modal triggers, transient UI signals
 */
export default function App() {
  const [isDarkMode, setIsDarkMode] = useDarkMode();
  const { flowStep, setFlowStep } = useRoute();
  const dragScroll = useDragScroll();
  const game = useGameState({ dragStateRef: dragScroll.dragState });
  const { phase, events, now } = useWeddingPhase();
  // Where the site is pointing right now — the game hands off here instead of
  // always to the RSVP.
  const focus = focusFor(phase);

  const user = { uid: 'guest' };

  return (
    <IconConfigProvider>
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 font-sans text-slate-800 dark:text-slate-100 overflow-x-hidden selection:bg-purple-200 transition-colors duration-500">
      <AnimatePresence>
        {game.showCelebration && <Celebration onClose={() => game.setShowCelebration(false)} />}
      </AnimatePresence>

      <main className="pt-24 lg:pt-24 pb-20 min-h-screen flex flex-col items-center justify-center px-4 sm:px-6">
        {flowStep === 'welcome' && (
          <WelcomeScreen
            isDarkMode={isDarkMode}
            phase={phase}
            events={events}
            now={now}
            onStartGame={() => setFlowStep('game')}
            onNavigate={(route) => setFlowStep(route)}
          />
        )}

        {flowStep === 'game' && (
          <Suspense fallback={null}>
            <GameScreen
              isDarkMode={isDarkMode}
              dragScroll={dragScroll}
              game={game}
              onContinue={() => setFlowStep(focus.route)}
              continueLabel={focus.label}
            />
          </Suspense>
        )}

        {flowStep === 'rsvp' && (
          <RSVPScreen
            isDarkMode={isDarkMode}
            gameState={game.gameState}
            showCelebration={game.showCelebration}
            onSuccess={game.markRsvpDone}
            onNavigate={(route) => setFlowStep(route)}
          />
        )}

        {flowStep === 'guestbook' && <GuestbookScreen isDarkMode={isDarkMode} user={user} />}

        {flowStep === 'admin' && (
          <Suspense fallback={null}>
            <AdminScreen onNavigate={(route) => setFlowStep(route)} />
          </Suspense>
        )}

        {flowStep === 'edit' && (
          <Suspense fallback={null}>
            <EditRsvpScreen isDarkMode={isDarkMode} onNavigate={(route) => setFlowStep(route)} />
          </Suspense>
        )}

        {flowStep === 'gallery' && (
          <Suspense fallback={null}>
            <GalleryScreen isDarkMode={isDarkMode} onNavigate={(route) => setFlowStep(route)} />
          </Suspense>
        )}

        {flowStep === 'photobooth' && (
          <Suspense fallback={null}>
            <PhotoBoothScreen isDarkMode={isDarkMode} onNavigate={(route) => setFlowStep(route)} />
          </Suspense>
        )}

        {flowStep === 'livestream' && (
          <Suspense fallback={null}>
            <LivestreamScreen isDarkMode={isDarkMode} onNavigate={(route) => setFlowStep(route)} />
          </Suspense>
        )}

        {flowStep === 'timeline' && (
          <Suspense fallback={null}>
            <TimelineScreen isDarkMode={isDarkMode} onNavigate={(route) => setFlowStep(route)} />
          </Suspense>
        )}

        <GlobalControls
          flowStep={flowStep}
          setFlowStep={setFlowStep}
          phase={phase}
          isDarkMode={isDarkMode}
          setIsDarkMode={setIsDarkMode}
          gameMistakes={game.gameState.mistakes}
          gameState={game.gameState}
          onResetGame={game.resetGame}
        />
      </main>

      {flowStep === 'game' && (
        <Suspense fallback={null}>
          <GameModals
            activeStory={game.activeStory}
            setActiveStory={game.setActiveStory}
            closeStory={game.closeStory}
            activeQuestion={game.activeQuestion}
            setActiveQuestion={game.setActiveQuestion}
            toastMessage={game.toastMessage}
            shake={game.shake}
            handleAnswer={game.handleAnswer}
            handleMistake={game.handleMistake}
          />
        </Suspense>
      )}
    </div>
    </IconConfigProvider>
  );
}
